import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatApiError } from '../api';
import { track } from '../analytics';
import { launchCheckout } from '../components/CheckoutLauncher';
import Disclaimer from '../components/Disclaimer';
import { useAuth } from '../auth';
import { paymentsEnabled } from '../config';

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState<'newebpay' | 'stripe' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Compliance Baseline (STA-128, STA-135) — explicit waiver of the
  // 7-day cooling-off period under 《通訊交易解除權合理例外情事
  // 適用準則》第 2 條. Gates both checkout buttons.
  const [refundAgreed, setRefundAgreed] = useState(false);

  const subscribe = async (provider: 'newebpay' | 'stripe') => {
    if (!user) {
      nav('/login');
      return;
    }
    if (!refundAgreed) {
      setError('請先勾選同意退費政策再繼續。');
      return;
    }
    setError(null);
    setLoading(provider);
    track('checkout_started', { provider });
    try {
      const result = await api.checkout(
        provider,
        provider === 'stripe' ? `${window.location.origin}/account?checkout=ok` : undefined,
        true,
      );
      launchCheckout(result);
    } catch (e) {
      setLoading(null);
      setError(formatApiError(e, '付款啟動失敗 — 請稍後再試或聯絡客服。'));
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-16">
      <div className="text-center mb-12">
        <div className="mp-eyebrow">PRICING</div>
        <h1 className="mp-display text-4xl md:text-5xl text-tarot-gold mt-3">方案</h1>
        <p className="text-tarot-muted mt-3">隨時可取消</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Plan
          title="Free"
          price="NT$0"
          period="永久免費"
          features={['每日 1 次抽牌', '完整解讀', '跨對話記憶', 'AI 導師選擇']}
          cta={null}
        />
        <Plan
          title="Premium"
          highlight
          price="NT$199"
          period="/ 月"
          features={['無限次抽牌', '完整解讀', '跨對話記憶', 'AI 導師選擇', '優先支援']}
          cta={
            paymentsEnabled ? (
              <div className="space-y-3">
                <RefundConsent
                  checked={refundAgreed}
                  onChange={setRefundAgreed}
                />
                <button
                  disabled={loading !== null || !refundAgreed}
                  onClick={() => subscribe('newebpay')}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading === 'newebpay' ? '啟動付款…' : '訂閱（信用卡 / LINE Pay / ATM）'}
                </button>
                <button
                  disabled={loading !== null || !refundAgreed}
                  onClick={() => subscribe('stripe')}
                  className="btn-ghost w-full text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading === 'stripe' ? '啟動付款…' : 'Pay with international card (USD)'}
                </button>
              </div>
            ) : (
              <ComingSoonCta />
            )
          }
        />
      </div>

      {error && <p className="mt-6 text-red-400 text-sm text-center">{error}</p>}

      <Disclaimer />
    </div>
  );
}

function ComingSoonCta() {
  return (
    <div className="space-y-3" data-testid="pricing-coming-soon">
      <button
        type="button"
        disabled
        className="btn-primary w-full opacity-60 cursor-not-allowed"
        aria-disabled="true"
      >
        敬請期待
      </button>
      <p className="text-xs text-tarot-muted leading-relaxed">
        付款服務即將上線（藍新金流審核中），上線後即可訂閱 Premium。
      </p>
    </div>
  );
}

function RefundConsent({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-2 text-xs leading-relaxed text-tarot-muted cursor-pointer bg-tarot-purple/10 border border-tarot-purple/30 rounded p-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 accent-tarot-gold flex-shrink-0"
        aria-label="同意退費政策"
      />
      <span>
        我了解並同意：本網站之數位占卜服務依《通訊交易解除權合理例外情事適用準則》第 2 條，屬「一經提供即為完成之線上服務」，付款並提供占卜結果後{' '}
        <strong className="text-tarot-text">不適用 7 天鑑賞期、無法退費</strong>。詳見
        <a
          href="/refund-policy"
          target="_blank"
          rel="noreferrer"
          className="underline mx-1 text-tarot-gold"
        >
          退費政策
        </a>
        。
      </span>
    </label>
  );
}

function Plan({
  title,
  price,
  period,
  features,
  cta,
  highlight = false,
}: {
  title: string;
  price: string;
  period: string;
  features: string[];
  cta: React.ReactNode | null;
  highlight?: boolean;
}) {
  return (
    <div
      className={
        'card flex flex-col ' +
        (highlight ? 'border-tarot-gold/60 ring-1 ring-tarot-gold/30' : '')
      }
    >
      <h3 className="text-tarot-gold text-xl">{title}</h3>
      <div className="mt-4">
        <span className="text-3xl">{price}</span>
        <span className="text-tarot-muted ml-2">{period}</span>
      </div>
      <ul className="mt-6 space-y-2 text-sm text-tarot-text flex-1">
        {features.map((f) => (
          <li key={f}>· {f}</li>
        ))}
      </ul>
      {cta && <div className="mt-6">{cta}</div>}
    </div>
  );
}
